import { format, parseISO, differenceInDays } from 'date-fns';

/**
 * Format a date string (YYYY-MM-DD) to a display format
 */
export function formatDate(dateString: string | null | undefined, formatStr = 'MMM d, yyyy'): string {
  if (!dateString) return '-';
  try {
    return format(parseISO(dateString), formatStr);
  } catch (error) {
    console.error('Invalid date:', dateString, error);
    return dateString;
  }
}

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 * This avoids timezone conversion issues when working with DATE-only fields
 */
export function getTodayDateString(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Get date string from Date object in YYYY-MM-DD format (local timezone)
 * Safe for DATE-only fields without timezone conversion
 */
export function toDateString(date: Date = new Date()): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Compare two date strings (YYYY-MM-DD format) without timezone conversion
 * Returns: -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareDateStrings(date1: string, date2: string): number {
  if (date1 === date2) return 0;
  return date1 < date2 ? -1 : 1;
}

/**
 * Check if a date string (YYYY-MM-DD) is in the future compared to today
 * Uses string comparison to avoid timezone issues
 */
export function isDateInFuture(dateString: string): boolean {
  const today = getTodayDateString();
  return compareDateStrings(dateString, today) > 0;
}

/**
 * Calculate days between two dates
 */
export function daysBetween(startDate: string, endDate: string | null = null): number {
  const start = parseISO(startDate);
  const end = endDate ? parseISO(endDate) : new Date();
  return differenceInDays(end, start);
}

/**
 * Determine color bucket based on threshold
 */
export function getThresholdColor(
  value: number,
  greenMax: number,
  yellowMax: number
): 'green' | 'yellow' | 'red' {
  if (value <= greenMax) return 'green';
  if (value <= yellowMax) return 'yellow';
  return 'red';
}

/**
 * Check if an order date was backdated by comparing it to the created_at timestamp
 * Returns true if the ordered_date is before the date portion of created_at
 *
 * @param orderedDate - The order date in YYYY-MM-DD format
 * @param createdAt - The timestamp when the order was created (ISO string with timezone)
 * @returns true if the order was backdated, false otherwise
 */
export function isOrderBackdated(
  orderedDate: string | null,
  createdAt: string | null
): boolean {
  if (!orderedDate || !createdAt) return false;

  try {
    // Extract the date portion (YYYY-MM-DD) from the created_at timestamp
    const createdDate = createdAt.split('T')[0];

    // Compare: if ordered_date < created_date, it was backdated
    return compareDateStrings(orderedDate, createdDate) < 0;
  } catch (error) {
    console.error('Error checking if order was backdated:', error);
    return false;
  }
}
